import { FileDropOverlay, useFileDrop } from "@/hooks/useFileDrop";
import { partitionFiles } from "@/lib/attachmentRules";
import { MentionTextarea } from "@/components/common/MentionTextarea";
import { TagPoolField } from "@/components/tags/TagPoolField";
import { StaffMessageAttachments } from "@/components/tickets/StaffMessageAttachments";
import { PendingFileRow } from "@/components/PendingFileRow";
import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import { TicketDescriptionCard } from "@/components/tickets/TicketDescriptionCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { linkifyText } from "@/lib/linkify";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { authService } from "@/services/auth.service/auth.api";
import { agentService } from "@/services/agent.service/agent.api";
import clientService from "@/services/client";
import companyService from "@/services/company";
import projectService from "@/services/project";
import { teamService } from "@/services/team.service/team.api";
import ticketService, { MessageType, Ticket, TicketAttachmentRef, TicketMessage } from "@/services/ticket";
import { AlarmClock, ArrowLeft, Bot, Check, CheckCircle2, Clock, Info, Mail, MessageSquare, Mic, Paperclip, Pencil, Phone, RefreshCw, RotateCcw, Send, Square, Trash2, User, UserPlus, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

function ticketMessageId(msg: { _id?: string; createdAt?: string }) {
  return String(msg._id ?? msg.createdAt);
}

/** Altura máxima do campo de nova mensagem antes de virar scroll interno (~7 linhas). */
const MESSAGE_TEXTAREA_MAX_HEIGHT = 160;

function resizeComposerTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, MESSAGE_TEXTAREA_MAX_HEIGHT)}px`;
}

/** Normaliza id vindo da API (string ou ObjectId serializado). */
function idFromApi(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "object" && v !== null && "$oid" in (v as Record<string, unknown>)) {
    return String((v as { $oid: string }).$oid);
  }
  return String(v);
}

function formatSlaMinutes(totalMinutes: number): string {
  const mins = Math.max(0, totalMinutes);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
}

/** Status da SLA de resposta do ticket — null quando não há SLA configurada para o Projeto+prioridade. */
function getSlaResponseInfo(
  ticket: Ticket | null
): { label: string; className: string; detail: string } | null {
  if (!ticket?.firstResponseDueAt) return null;
  const dueAt = new Date(ticket.firstResponseDueAt).getTime();

  if (ticket.firstResponseAt) {
    const respondedAt = new Date(ticket.firstResponseAt).getTime();
    const onTime = respondedAt <= dueAt;
    return {
      label: onTime ? "Respondida no prazo" : "Respondida com atraso",
      className: onTime
        ? "bg-green-500/10 text-green-600 border-green-500/20"
        : "bg-red-500/10 text-red-600 border-red-500/20",
      detail: `Primeira resposta às ${new Date(ticket.firstResponseAt).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })}`,
    };
  }

  const now = Date.now();
  const riskAt = ticket.firstResponseRiskAt ? new Date(ticket.firstResponseRiskAt).getTime() : dueAt;
  if (now >= dueAt) {
    return {
      label: "SLA de resposta estourada",
      className: "bg-red-500/10 text-red-600 border-red-500/20",
      detail: `Estourou há ${formatSlaMinutes(Math.round((now - dueAt) / 60_000))}`,
    };
  }
  if (now >= riskAt) {
    return {
      label: "SLA de resposta em risco",
      className: "bg-amber-500/10 text-amber-600 border-amber-500/20",
      detail: `Faltam ${formatSlaMinutes(Math.round((dueAt - now) / 60_000))} para o prazo`,
    };
  }
  return {
    label: "SLA de resposta no prazo",
    className: "bg-green-500/10 text-green-600 border-green-500/20",
    detail: `Prazo em ${formatSlaMinutes(Math.round((dueAt - now) / 60_000))}`,
  };
}

/** Só renderiza quando há SLA de resposta resolvida para este ticket (Projeto+prioridade). */
function SlaResponseBadge({ ticket }: { ticket: Ticket | null }) {
  const info = getSlaResponseInfo(ticket);
  if (!info) return null;
  return (
    <div className="space-y-2">
      <Label className="text-sm flex items-center gap-1.5">
        <AlarmClock className="h-3.5 w-3.5" />
        SLA de resposta
      </Label>
      <div className={`rounded-md border px-3 py-2 text-sm ${info.className}`}>
        <div className="font-medium">{info.label}</div>
        <div className="mt-0.5 text-xs opacity-80">{info.detail}</div>
      </div>
    </div>
  );
}

export default function TicketDetails() {
    const { protocolId } = useParams();
    const { toast } = useToast();
    const navigate = useNavigate();
    // Volta pro histórico (preserva os filtros da lista de onde o usuário veio) — só cai pro
    // /tickets "limpo" quando não há entrada anterior no app (ex.: aba aberta direto no ticket).
    const handleBackToTickets = () => {
        const idx = (window.history.state as { idx?: number } | null)?.idx;
        if (typeof idx === "number" && idx > 0) {
            navigate(-1);
        } else {
            navigate("/tickets");
        }
    };
    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [messages, setMessages] = useState<TicketMessage[]>([]);
    const [newMessage, setNewMessage] = useState("");
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [messageType, setMessageType] = useState<"public" | "private">("public");
    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editingText, setEditingText] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
    const [selectedAgent, setSelectedAgent] = useState("");
    const [selectedClient, setSelectedClient] = useState("");
    const [selectedProject, setSelectedProject] = useState("");
    const [selectedContact, setSelectedContact] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedTeam, setSelectedTeam] = useState("");
    const [isResolutionDialogOpen, setIsResolutionDialogOpen] = useState(false);
    const [resolutionTime, setResolutionTime] = useState({ hours: "", minutes: "" });
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isReopening, setIsReopening] = useState(false);
    const [hasNewMessages, setHasNewMessages] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [showTicketInfo, setShowTicketInfo] = useState(true);
    const [updateProgress, setUpdateProgress] = useState(0);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesEndMobileRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const composerTextareaDesktopRef = useRef<HTMLTextAreaElement>(null);
    const composerTextareaMobileRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        resizeComposerTextarea(composerTextareaDesktopRef.current);
        resizeComposerTextarea(composerTextareaMobileRef.current);
    }, [newMessage]);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const progressIntervalRef = useRef<NodeJS.Timeout>();

    // Estados para os seletores
    const [agents, setAgents] = useState<any[]>([]);
    const [companies, setCompanies] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [contacts, setContacts] = useState<any[]>([]);
    const [teams, setTeams] = useState<any[]>([]);

    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordChunksRef = useRef<Blob[]>([]);
    const recordStreamRef = useRef<MediaStream | null>(null);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const loggedUserId = authService.getCurrentUser()?._id;

    // Estado para a seção "Pessoas no ticket"
    const [addMembersOpen, setAddMembersOpen] = useState(false);
    const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
    const [memberSearch, setMemberSearch] = useState("");
    const [isAddingMembers, setIsAddingMembers] = useState(false);

    useEffect(() => {
        loadTicket();
        loadMessages();
        loadTeams();
        loadAgents();
        loadCompanies();
    }, [protocolId]);

    // Carrega projetos/pessoas da empresa selecionada (em cascata)
    useEffect(() => {
        loadProjects(selectedClient);
        loadContacts(selectedClient);
    }, [selectedClient]);

    // Adicionar polling para mensagens
    useEffect(() => {
        let progress = 0;
        const progressStep = 20; // 20% por segundo (100% / 5 segundos)

        const interval = setInterval(async () => {
            if (!protocolId || isPolling) return;

            try {
                setIsPolling(true);
                const [data, error] = await ticketService.getMessages(protocolId, 1);

                if (error) {
                    console.error("Erro ao atualizar mensagens:", error);
                    return;
                }

                if (data?.result) {
                    const newMessages = data.result.map(msg => ({
                        _id: ticketMessageId(msg),
                        ticketId: protocolId,
                        content: msg.content,
                        type: msg.type as MessageType,
                        createdAt: msg.createdAt,
                        editedAt: msg.editedAt,
                        deletedAt: msg.deletedAt,
                        contact: msg.contact,
                        agent: msg.agent
                    }));

                    // Verifica se há novas mensagens
                    const currentMessageIds = new Set(messages.map(m => m._id));
                    const onlyNewMessages = newMessages
                        .filter(msg => !currentMessageIds.has(msg._id))
                        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

                    if (onlyNewMessages.length > 0) {
                        setMessages(prev => [...prev, ...onlyNewMessages]);

                        // Se a última mensagem não for do agente atual, mostra o balão
                        const lastMessage = onlyNewMessages[onlyNewMessages.length - 1];
                        if (lastMessage.type !== MessageType.AGENT && !isAtBottom) {
                            setHasNewMessages(true);
                        } else {
                            // Se estiver no final, faz scroll automático
                            setTimeout(() => {
                                scrollToBottom();
                            }, 100);
                        }
                    }
                }
            } finally {
                setIsPolling(false);
                progress = 0; // Reinicia o progresso após cada atualização
                setUpdateProgress(0);
            }
        }, 5000);

        // Atualiza a barra de progresso a cada segundo
        const progressInterval = setInterval(() => {
            progress = Math.min(progress + progressStep, 100);
            setUpdateProgress(progress);
        }, 1000);

        return () => {
            clearInterval(interval);
            clearInterval(progressInterval);
        };
    }, [protocolId, messages.length, isAtBottom, isPolling]);

    // Carregar equipes
    const loadTeams = async () => {
        const [data, error] = await teamService.getAll({}, undefined, undefined);
        if (error) {
            toast({
                title: "Erro ao carregar equipes",
                description: "Não foi possível carregar a lista de equipes.",
                variant: "destructive",
                className: "bg-destructive text-destructive-foreground"
            });
            return;
        }
        if (data) {
            setTeams(data.result || []);
        }
    };

    // Carregar responsáveis (admin + agent)
    const loadAgents = async () => {
        const [data, error] = await agentService.getResponsibles({}, undefined, undefined);
        if (error) {
            toast({
                title: "Erro ao carregar responsáveis",
                description: "Não foi possível carregar a lista de responsáveis.",
                variant: "destructive",
                className: "bg-destructive text-destructive-foreground"
            });
            return;
        }
        if (data) {
            setAgents(data.result || []);
        }
    };

    // Carregar empresas
    const loadCompanies = async () => {
        const [data, error] = await companyService.getAll(undefined, 1, 500);
        if (error) {
            toast({
                title: "Erro ao carregar empresas",
                description: "Não foi possível carregar a lista de empresas.",
                variant: "destructive",
                className: "bg-destructive text-destructive-foreground"
            });
            return;
        }
        if (data) {
            setCompanies(data.result || []);
        }
    };

    // Carregar projetos de uma empresa
    const loadProjects = async (companyId: string) => {
        if (!companyId) {
            setProjects([]);
            return;
        }
        const [data] = await projectService.getAllByClient(companyId, undefined, 1, 500);
        setProjects(data?.result || []);
    };

    // Carregar pessoas (contatos) de uma empresa
    const loadContacts = async (companyId: string) => {
        if (!companyId) {
            setContacts([]);
            return;
        }
        const [data] = await clientService.getAll(undefined, 1, 500, undefined, companyId);
        setContacts(data?.result || []);
    };

    const loadTicket = async () => {
        if (!protocolId) return;

        setIsInitialLoading(true);
        try {
            // Carrega o ticket
            const [ticketData, ticketError] = await ticketService.getById(protocolId);
            if (ticketError) {
                throw new Error("Não foi possível carregar os detalhes do ticket.");
            }

            setTicket(ticketData);

            // Atualiza os estados com os dados do ticket
            setSelectedAgent(idFromApi(ticketData.agentId) || "");
            setSelectedClient(idFromApi(ticketData.clientId));
            setSelectedProject(idFromApi(ticketData.projectId) || "");
            setSelectedContact(idFromApi(ticketData.contactId) || "");
            setSelectedStatus(ticketData.status);
            setSelectedTeam(idFromApi(ticketData.teamId) || "");
            setSelectedTags(
                Array.isArray(ticketData.tags)
                    ? ticketData.tags.map((t) => String(t).toLowerCase())
                    : []
            );

            // Mantém a lista completa de responsáveis para permitir atribuir admin ou agent
        } catch (error) {
            toast({
                title: "Erro ao carregar ticket",
                description: error instanceof Error ? error.message : "Não foi possível carregar os detalhes do ticket.",
                variant: "destructive",
                className: "bg-destructive text-destructive-foreground"
            });
            navigate("/tickets");
        } finally {
            setIsInitialLoading(false);
        }
    };

    useEffect(() => {
        if (!protocolId) return;
        const id = window.setInterval(async () => {
            const [data, err] = await ticketService.getById(protocolId);
            if (!data || err) return;
            // Mantém o ticket fresco para o cabeçalho/badges, mas NÃO sobrescreve
            // os campos do formulário — senão o poll atropela a edição em andamento
            // do usuário (ex.: reverte o status antes de ele clicar em "Atualizar").
            setTicket(data);
        }, 5000);
        return () => clearInterval(id);
    }, [protocolId]);

    useEffect(() => {
        if (!ticket?.clientId) return;
        const cid = idFromApi(ticket.clientId);
        if (!cid) return;
        setCompanies((prev) => {
            if (prev.some((c) => String(c._id) === cid)) return prev;
            const name = ticket.clientName?.trim() || "Empresa";
            return [{ _id: cid, name }, ...prev];
        });
    }, [ticket?.clientId, ticket?.clientName, ticket?._id]);

    useEffect(() => {
        if (!ticket?.projectId) return;
        const pid = idFromApi(ticket.projectId);
        if (!pid) return;
        setProjects((prev) => {
            if (prev.some((p) => String(p._id) === pid)) return prev;
            const name = ticket.projectName?.trim() || "Projeto";
            return [{ _id: pid, name }, ...prev];
        });
    }, [ticket?.projectId, ticket?.projectName, ticket?._id]);

    useEffect(() => {
        if (!ticket?.contactId) return;
        const cid = idFromApi(ticket.contactId);
        if (!cid) return;
        setContacts((prev) => {
            if (prev.some((c) => String(c._id) === cid)) return prev;
            const name = ticket.contactName?.trim() || "Pessoa";
            return [{ _id: cid, name }, ...prev];
        });
    }, [ticket?.contactId, ticket?.contactName, ticket?._id]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        messagesEndMobileRef.current?.scrollIntoView({ behavior: "smooth" });
        setHasNewMessages(false);
    };

    const handleScroll = () => {
        if (!messagesContainerRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const isBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
        setIsAtBottom(isBottom);

        // Carrega mais mensagens quando chegar ao topo
        if (scrollTop === 0 && hasMore && !isLoadingMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            loadMessages(nextPage, true);
        }

        // Se estiver no final, remove o balão de novas mensagens
        if (isBottom) {
            setHasNewMessages(false);
        }
    };

    const loadMessages = async (pageNumber = 1, append = false) => {
        if (!protocolId) return;

        if (pageNumber === 1) {
            setIsInitialLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            const [data, error] = await ticketService.getMessages(protocolId, pageNumber);

            if (error) {
                toast({
                    title: "Erro ao carregar mensagens",
                    description: "Não foi possível carregar as mensagens do ticket.",
                    variant: "destructive"
                });
                return;
            }

            if (data?.result) {
                const newMessages = data.result.map(msg => ({
                    _id: ticketMessageId(msg),
                    ticketId: protocolId,
                    content: msg.content,
                    type: msg.type as MessageType,
                    createdAt: msg.createdAt,
                    editedAt: msg.editedAt,
                    deletedAt: msg.deletedAt,
                    contact: msg.contact,
                    agent: msg.agent
                }));

                // Verifica se há novas mensagens que não foram enviadas pelo agente atual
                const hasNewIncomingMessages = !append && newMessages.length > messages.length &&
                    newMessages[newMessages.length - 1].type !== MessageType.AGENT;

                // API: createdAt ASC — mais antiga primeiro, mais recente por último
                const orderedMessages = append ? [...messages, ...newMessages] : newMessages;

                setMessages(orderedMessages);
                setHasMore(data.result.length === 10);

                if (hasNewIncomingMessages && !isAtBottom) {
                    setHasNewMessages(true);
                }

                // Se não estiver carregando mais mensagens antigas, faz scroll para o final
                if (!append && !isAtBottom) {
                    setTimeout(() => {
                        scrollToBottom();
                    }, 100);
                }
            } else {
                setMessages([]);
                setHasMore(false);
            }
        } finally {
            setIsInitialLoading(false);
            setIsLoadingMore(false);
        }
    };

    useLayoutEffect(() => {
        if (messages.length > 0 && !isInitialLoading && page === 1) {
            const scrollBoth = () => {
                messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
                messagesEndMobileRef.current?.scrollIntoView({ behavior: "auto" });
            };
            const raf = requestAnimationFrame(scrollBoth);
            const t2 = setTimeout(scrollBoth, 200);
            return () => {
                cancelAnimationFrame(raf);
                clearTimeout(t2);
            };
        }
    }, [messages.length, isInitialLoading, page]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        const [data, error] = await ticketService.getMessages(protocolId, 1);

        if (error) {
            toast({
                title: "Erro ao atualizar mensagens",
                description: "Não foi possível atualizar as mensagens do ticket.",
                variant: "destructive"
            });
            setIsRefreshing(false);
            return;
        }

        if (data?.result) {
            const newMessages = data.result.map(msg => ({
                _id: ticketMessageId(msg),
                ticketId: protocolId,
                content: msg.content,
                type: msg.type as MessageType,
                createdAt: msg.createdAt,
                editedAt: msg.editedAt,
                deletedAt: msg.deletedAt,
                contact: msg.contact,
                agent: msg.agent
            }));

            // Verifica se há novas mensagens que não foram enviadas pelo agente atual
            const hasNewIncomingMessages = newMessages.length > messages.length &&
                newMessages[newMessages.length - 1].type !== MessageType.AGENT;

            // Mantém as mensagens antigas e adiciona apenas as novas
            const currentMessageIds = new Set(messages.map(m => m._id));
            const onlyNewMessages = newMessages
                .filter(msg => !currentMessageIds.has(msg._id))
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

            if (onlyNewMessages.length > 0) {
                setMessages(prev => [...prev, ...onlyNewMessages]);

                if (!isAtBottom) {
                    setHasNewMessages(true);
                }
            }
        }
        setIsRefreshing(false);
    };

    useEffect(() => {
        return () => {
            recordStreamRef.current?.getTracks().forEach((tr) => tr.stop());
        };
    }, []);

    const startAgentRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            recordStreamRef.current = stream;
            recordChunksRef.current = [];
            const mr = new MediaRecorder(stream);
            mr.ondataavailable = (ev) => {
                if (ev.data.size > 0) recordChunksRef.current.push(ev.data);
            };
            mr.onstop = () => {
                stream.getTracks().forEach((tr) => tr.stop());
                recordStreamRef.current = null;
                const blob = new Blob(recordChunksRef.current, { type: mr.mimeType || "audio/webm" });
                const file = new File([blob], `gravacao-${Date.now()}.webm`, {
                    type: blob.type || "audio/webm",
                });
                setPendingFiles((prev) => [...prev, file]);
                toast({ title: "Áudio gravado", description: "Envie a mensagem para anexar." });
            };
            mr.start(200);
            mediaRecorderRef.current = mr;
            setIsRecording(true);
        } catch {
            toast({
                title: "Microfone",
                description: "Não foi possível acessar o microfone.",
                variant: "destructive",
            });
        }
    };

    const stopAgentRecording = () => {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") mr.stop();
        mediaRecorderRef.current = null;
        setIsRecording(false);
    };

    /** Cmd/Ctrl+V no campo de mensagem: anexa imagens coladas da área de transferência. */
    const handlePasteImages = (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const items = Array.from(e.clipboardData?.items || []);
        const images = items
            .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
            .map((item) => item.getAsFile())
            .filter((file): file is File => !!file);
        if (!images.length) return;
        e.preventDefault();
        setPendingFiles((prev) => [...prev, ...images]);
    };

    /** Arrastar e soltar arquivos em qualquer ponto da tela do ticket vira anexo pendente. */
    const handleDroppedFiles = (files: File[]) => {
        const { accepted, errors } = partitionFiles(files, "ticket");
        if (errors.length) toast({ title: "Arquivo não anexado", description: errors.join(" "), variant: "destructive" });
        if (accepted.length) setPendingFiles((prev) => [...prev, ...accepted]);
    };
    const { isDragging, dropProps } = useFileDrop(handleDroppedFiles, isRecording);

    const handleSendMessage = async () => {
        if (!protocolId) return;
        if (isRecording) return;
        if (!newMessage.trim() && pendingFiles.length === 0) return;

        setIsSendingMessage(true);
        const attachments: TicketAttachmentRef[] = [];
        for (const file of pendingFiles) {
            const [ref, err] = await ticketService.uploadAttachment(file);
            if (err?.msg || !ref) {
                toast({
                    title: "Upload",
                    description: err?.msg || "Falha no envio do arquivo",
                    variant: "destructive",
                });
                setIsSendingMessage(false);
                return;
            }
            attachments.push(ref);
        }

        const mt = messageType === "private" ? MessageType.PRIVATE_NOTE : MessageType.AGENT;
        const text = newMessage.trim();
        const payload: string | { msg?: string; attachments: TicketAttachmentRef[] } =
            attachments.length > 0 ? (text ? { msg: text, attachments } : { attachments }) : text;

        const [data, error] = await ticketService.sendMessage(protocolId, payload, mt);

        if (error) {
            toast({
                title: "Erro ao enviar mensagem",
                description: "Não foi possível enviar a mensagem.",
                variant: "destructive",
            });
            setIsSendingMessage(false);
            return;
        }

        if (data?.result) {
            const newMessageObj = {
                _id: ticketMessageId(data.result),
                ticketId: protocolId,
                content: data.result.content,
                type: data.result.type as MessageType,
                createdAt: data.result.createdAt,
                contact: data.result.contact,
                agent: data.result.agent,
            };
            setMessages((prev) => [...prev, newMessageObj]);
        }

        setNewMessage("");
        setPendingFiles([]);
        setIsSendingMessage(false);
        scrollToBottom();
    };

    const handleUpdateTicket = async (additionalData = {}) => {
        if (!protocolId) return;

        const updateData = {
            agentId: selectedAgent,
            clientId: selectedClient,
            projectId: selectedProject,
            contactId: selectedContact,
            status: selectedStatus,
            teamId: selectedTeam,
            tags: selectedTags,
            ...additionalData
        };

        const [data, error] = await ticketService.update(protocolId, updateData);

        if (error) {
            toast({
                title: "Erro ao atualizar ticket",
                description: "Não foi possível atualizar o ticket.",
                variant: "destructive",
                className: "bg-destructive text-destructive-foreground"
            });
            return;
        }

        setTicket(data);
        toast({
            title: "Ticket atualizado",
            description: "O ticket foi atualizado com sucesso.",
            className: "bg-primary text-primary-foreground"
        });
    };

    const handleReopenTicket = async () => {
        if (!protocolId) return;
        setIsReopening(true);
        // O status vai explícito no payload porque o setState do select ainda não refletiu aqui.
        const [data, error] = await ticketService.update(protocolId, { status: "open" });
        setIsReopening(false);

        if (error) {
            toast({
                title: "Erro ao reabrir ticket",
                description: "Não foi possível reabrir o ticket.",
                variant: "destructive",
                className: "bg-destructive text-destructive-foreground"
            });
            return;
        }

        setTicket(data);
        setSelectedStatus("open");
        toast({
            title: "Ticket reaberto",
            description: "O ticket voltou para o status Aberto.",
            className: "bg-primary text-primary-foreground"
        });
    };

    const handleResolution = async () => {
        if (!protocolId) return;

        const h = parseInt(String(resolutionTime.hours).trim(), 10);
        const m = parseInt(String(resolutionTime.minutes).trim(), 10);
        const hours = Number.isFinite(h) && h >= 0 ? h : 0;
        const minutes = Number.isFinite(m) && m >= 0 ? Math.min(59, m) : 0;
        const totalMinutes = hours * 60 + minutes;

        await handleUpdateTicket({ resolutionTime: totalMinutes });

        setIsResolutionDialogOpen(false);
        setResolutionTime({ hours: "", minutes: "" });
    };

    const handleAddMembers = async () => {
        if (!ticket || selectedMemberIds.length === 0) return;
        setIsAddingMembers(true);
        const [data, error] = await ticketService.addMembers(ticket._id, selectedMemberIds);
        setIsAddingMembers(false);
        if (error) {
            toast({
                title: "Erro ao adicionar pessoas",
                description: "Não foi possível adicionar as pessoas ao ticket.",
                variant: "destructive",
            });
            return;
        }
        if (data) {
            setTicket(data);
            setAddMembersOpen(false);
            setSelectedMemberIds([]);
            setMemberSearch("");
            toast({ title: "Pessoas adicionadas", description: "Pessoas adicionadas ao ticket com sucesso." });
        }
    };

    const handleRemoveMember = async (userId: string) => {
        if (!ticket) return;
        const [data, error] = await ticketService.removeMember(ticket._id, userId);
        if (error) {
            toast({
                title: "Erro ao remover pessoa",
                description: "Não foi possível remover a pessoa do ticket.",
                variant: "destructive",
            });
            return;
        }
        if (data) setTicket(data);
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "open":
                return <Badge variant="outline" className="bg-ticket-open/10 text-ticket-open border-ticket-open/20">Aberto</Badge>;
            case "in_progress":
                return <Badge variant="outline" className="bg-ticket-pending/10 text-ticket-pending border-ticket-pending/20">Em Atendimento</Badge>;
            case "resolved":
                return <Badge variant="outline" className="bg-ticket-resolved/10 text-ticket-resolved border-ticket-resolved/20">Fechado</Badge>;
        }
    };

    const isOwnMessage = (message: any) => {
        if (!loggedUserId) return false;
        const agentId = message?.agent?._id ? String(message.agent._id) : "";
        const contactId = message?.contact?._id ? String(message.contact._id) : "";
        if (message.type === MessageType.AGENT) return agentId === String(loggedUserId);
        if (message.type === MessageType.USER) return contactId === String(loggedUserId);
        return false;
    };

    // Só o próprio autor edita/exclui — mesmo mensagem pública ou nota privada, nunca de outra
    // pessoa, e uma mensagem já excluída não tem mais o que editar/excluir de novo.
    const canEditMessage = (message: any) => {
        if (!loggedUserId || message.deletedAt) return false;
        if (message.type !== MessageType.AGENT && message.type !== MessageType.PRIVATE_NOTE) return false;
        const agentId = message?.agent?._id ? String(message.agent._id) : "";
        return agentId === String(loggedUserId);
    };

    const handleDeleteMessage = async (message: TicketMessage) => {
        if (!protocolId) return;
        if (!window.confirm("Excluir esta mensagem? Ela vai aparecer como \"Mensagem excluída\" pra quem já viu a conversa.")) return;
        setDeletingMessageId(message._id);
        const [data, error] = await ticketService.deleteMessage(protocolId, message._id);
        setDeletingMessageId(null);
        if (error) {
            toast({ title: "Erro ao excluir mensagem", description: error.msg || "Tente novamente.", variant: "destructive" });
            return;
        }
        if (data?.result) {
            const updated = data.result as TicketMessage;
            setMessages((prev) => prev.map((m) => (m._id === updated._id ? { ...m, ...updated } : m)));
        }
    };

    const startEditMessage = (message: TicketMessage) => {
        setEditingMessageId(message._id);
        setEditingText(message.content?.msg || "");
    };

    const cancelEditMessage = () => {
        setEditingMessageId(null);
        setEditingText("");
    };

    const saveEditMessage = async () => {
        if (!editingMessageId || !protocolId) return;
        const trimmed = editingText.trim();
        if (!trimmed) {
            toast({ title: "Erro", description: "A mensagem não pode ficar vazia", variant: "destructive" });
            return;
        }
        setSavingEdit(true);
        const [data, error] = await ticketService.editMessage(protocolId, editingMessageId, trimmed);
        setSavingEdit(false);
        if (error) {
            toast({ title: "Erro ao editar mensagem", description: error.msg || "Tente novamente.", variant: "destructive" });
            return;
        }
        if (data?.result) {
            const updated = data.result as TicketMessage;
            setMessages((prev) => prev.map((m) => (m._id === updated._id ? { ...m, ...updated } : m)));
        }
        setEditingMessageId(null);
        setEditingText("");
    };

    const getPriorityBadge = (priority: string) => {
        switch (priority) {
            case "low":
                return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">Baixa</Badge>;
            case "medium":
                return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Média</Badge>;
            case "high":
                return <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500/20">Alta</Badge>;
            case "urgent":
                return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">Urgente</Badge>;
        }
    };

    const formatMessage = (text: string) => {
        // Primeiro, vamos separar o texto e as tags especiais
        const parts = text?.split(/(<image>.*?<\/image>|<video>.*?<\/video>|<audio>.*?<\/audio>|<file>.*?<\/file>|<sticker>.*?<\/sticker>|<location>.*?<\/location>|<contact>.*?<\/contact>)/g);

        return parts?.map((part, index) => {
            // Se for uma tag de imagem
            if (part.startsWith('<image>') && part.endsWith('</image>')) {
                const imageUrl = part.slice(7, -8);
                return (
                    <div key={index} className="mt-2">
                        <div className="relative group">
                            <img
                                src={imageUrl}
                                alt="Imagem da mensagem"
                                className="max-w-full rounded-lg shadow-sm"
                                style={{ maxHeight: '300px', objectFit: 'contain' }}
                            />
                            <a
                                href={imageUrl}
                                download
                                target="_blank"
                                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Baixar imagem"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            </a>
                        </div>
                    </div>
                );
            }
            // Se for uma tag de vídeo
            else if (part.startsWith('<video>') && part.endsWith('</video>')) {
                const videoUrl = part.slice(7, -8);
                return (
                    <div key={index} className="mt-2">
                        <div className="relative group">
                            <video
                                src={videoUrl}
                                controls
                                className="max-w-full rounded-lg shadow-sm"
                                style={{ maxHeight: '300px' }}
                            />
                            <a
                                href={videoUrl}
                                download
                                target="_blank"
                                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Baixar vídeo"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            </a>
                        </div>
                    </div>
                );
            }
            // Se for uma tag de áudio
            else if (part.startsWith('<audio>') && part.endsWith('</audio>')) {
                const audioUrl = part.slice(7, -8);
                return (
                    <div key={index} className="mt-2">
                        <div className="relative group">
                            <audio
                                src={audioUrl}
                                controls
                                className="w-full"
                            />
                            <a
                                href={audioUrl}
                                download
                                target="_blank"
                                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Baixar áudio"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            </a>
                        </div>
                    </div>
                );
            }
            // Se for uma tag de arquivo
            else if (part.startsWith('<file>') && part.endsWith('</file>')) {
                const fileUrl = part.slice(6, -7);
                const fileName = decodeURIComponent(fileUrl.split('/').pop() || 'Arquivo');

                return (
                    <div key={index} className="mt-2">
                        <a
                            href={fileUrl}
                            download
                            target="_blank"
                            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                    <polyline points="10 9 9 9 8 9" />
                                </svg>
                                <span className="text-sm truncate">{fileName}</span>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        </a>
                    </div>
                );
            }
            // Se for uma tag de sticker
            else if (part.startsWith('<sticker>') && part.endsWith('</sticker>')) {
                const stickerUrl = part.slice(9, -10);
                return (
                    <div key={index} className="mt-2">
                        <div className="relative group">
                            <img
                                src={stickerUrl}
                                alt="Sticker"
                                className="max-w-[200px] rounded-lg shadow-sm"
                            />
                            <a
                                href={stickerUrl}
                                download
                                target="_blank"
                                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Baixar sticker"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            </a>
                        </div>
                    </div>
                );
            }
            // Se for uma tag de localização
            else if (part.startsWith('<location>') && part.endsWith('</location>')) {
                const locationUrl = part.slice(10, -11);
                return (
                    <div key={index} className="mt-2">
                        <a
                            href={locationUrl}
                            target="_blank"
                            className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                <circle cx="12" cy="10" r="3" />
                            </svg>
                            <span className="text-sm">Ver localização</span>
                        </a>
                    </div>
                );
            }
            // Se for uma tag de contato
            else if (part.startsWith('<contact>') && part.endsWith('</contact>')) {
                const contactUrl = part.slice(9, -10);
                return (
                    <div key={index} className="mt-2">
                        <a
                            href={contactUrl}
                            target="_blank"
                            className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            <span className="text-sm">Ver contato</span>
                        </a>
                    </div>
                );
            }

            // Se for texto normal, aplica a formatação de negrito + linkifica URLs
            return part.split(/(\*[^*]+\*)/g).map((textPart, textIndex) => {
                if (textPart.startsWith('*') && textPart.endsWith('*')) {
                    return <strong key={`${index}-${textIndex}`}>{textPart.slice(1, -1)}</strong>;
                }
                return linkifyText(textPart);
            });
        });
    };


    if (isInitialLoading) {
        return (
            <div className="flex h-screen">

                <main className="flex-1 p-6 overflow-auto">
                    <LoadingOverlay isLoading={isInitialLoading} />
                </main>
            </div>
        );
    }

    if (!ticket) {
        return null;
    }

    // Pessoas disponíveis para adicionar ao ticket (exclui membros atuais e o contato principal)
    const availableMembersToAdd = contacts
        .filter((c) => {
            const cid = String(c._id);
            const alreadyMember = (ticket.memberIds || []).some((mid) => String(mid) === cid);
            const isMainContact = cid === String(idFromApi(ticket.contactId));
            return !alreadyMember && !isMainContact;
        })
        .filter((c) => {
            const term = memberSearch.trim().toLowerCase();
            if (!term) return true;
            return String(c.name || "").toLowerCase().includes(term);
        });

    return (
        <div className="h-full flex flex-col overflow-hidden relative" {...dropProps}>
            <FileDropOverlay visible={isDragging} label="Solte para anexar à resposta" />
            <div className="flex items-center justify-between mb-4 relative">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={handleBackToTickets} className="hover:bg-transparent">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-lg font-semibold">#{ticket.protocolId}</h1>
                            {getPriorityBadge(ticket.priority)}
                            {getStatusBadge(ticket.status)}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">{ticket.title}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {ticket.status === "resolved" && (
                        <Button variant="outline" size="sm" onClick={handleReopenTicket} disabled={isReopening}>
                            <RotateCcw className={`h-4 w-4 mr-1.5 ${isReopening ? "animate-spin" : ""}`} />
                            {isReopening ? "Reabrindo…" : "Reabrir ticket"}
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="hover:bg-transparent"
                    >
                        <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
                    </Button>
                </div>
            </div>

            <TicketDescriptionCard description={ticket.description} className="mb-4" />

            {/* Layout Desktop */}
            <div className="hidden lg:grid grid-cols-4 gap-0 flex-1 h-[calc(100vh-8rem)] overflow-hidden">
                {/* Chat */}
                <div className="col-span-3 h-full overflow-auto border-r">
                    <div className="flex flex-col h-full">
                        <div className="flex-1 overflow-y-auto mb-4 px-6" ref={messagesContainerRef} onScroll={handleScroll}>
                            {isLoadingMore && (
                                <div className="flex justify-center py-4">
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                </div>
                            )}
                            {messages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                                    <MessageSquare className="h-8 w-8 mb-2" />
                                    <p>Nenhuma mensagem ainda</p>
                                </div>
                            ) : (
                                <div className="space-y-3 py-4">
                                    {messages.map((message) => (
                                        <div
                                            key={message._id}
                                            className={`p-3 rounded-lg ${message.type === MessageType.PRIVATE_NOTE
                                                ? "w-full bg-yellow-50 border border-yellow-200"
                                                : message.type === MessageType.SYSTEM
                                                    ? "bg-purple-100 border border-purple-200 mr-auto max-w-[85%]"
                                                    : isOwnMessage(message)
                                                        ? "bg-primary/5 dark:bg-primary/10 border border-primary/15 dark:border-primary/20 ml-auto max-w-[85%]"
                                                        : "bg-muted/50 mr-auto max-w-[85%]"
                                                }`}
                                        >
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                {message.type === MessageType.SYSTEM ? (
                                                    <div className="flex items-center gap-2">
                                                        <Bot className="h-4 w-4 text-purple-600" />
                                                        <span className="text-sm font-medium text-purple-600">IA</span>
                                                    </div>
                                                ) : message.type === MessageType.AGENT ? (
                                                    <div className="flex items-center gap-2">
                                                        <User className="h-4 w-4 text-primary" />
                                                        <span className="text-sm font-medium text-primary">
                                                            {message.agent?.name || "Agente"}
                                                        </span>
                                                    </div>
                                                ) : message.type === MessageType.PRIVATE_NOTE ? (
                                                    <div className="flex items-center gap-2">
                                                        <MessageSquare className="h-4 w-4 text-yellow-600" />
                                                        <span className="text-sm font-medium text-yellow-600">
                                                            Nota Privada
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-sm font-medium text-muted-foreground">
                                                            {message.contact?.name || "Cliente"}
                                                        </span>
                                                    </div>
                                                )}
                                                {canEditMessage(message) && editingMessageId !== message._id && (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => startEditMessage(message)}
                                                            className="text-muted-foreground hover:text-foreground"
                                                            aria-label="Editar mensagem"
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteMessage(message)}
                                                            disabled={deletingMessageId === message._id}
                                                            className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                                                            aria-label="Excluir mensagem"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {message.deletedAt ? (
                                                <p className="text-sm italic text-muted-foreground">Mensagem excluída</p>
                                            ) : editingMessageId === message._id ? (
                                                <div className="space-y-2">
                                                    <textarea
                                                        className="w-full text-sm rounded-md border border-input bg-background p-2 resize-none"
                                                        rows={3}
                                                        value={editingText}
                                                        onChange={(e) => setEditingText(e.target.value)}
                                                        autoFocus
                                                    />
                                                    <div className="flex gap-2">
                                                        <Button size="sm" className="h-7 gap-1" disabled={savingEdit} onClick={saveEditMessage}>
                                                            <Check className="h-3 w-3" />
                                                            Salvar
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="h-7 gap-1" disabled={savingEdit} onClick={cancelEditMessage}>
                                                            <X className="h-3 w-3" />
                                                            Cancelar
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {message.content?.msg ? (
                                                        <p className={`text-sm whitespace-pre-wrap ${message.type === MessageType.PRIVATE_NOTE
                                                            ? "text-yellow-800"
                                                            : message.type === MessageType.SYSTEM
                                                                ? "text-purple-800"
                                                                : ""
                                                            }`}>{formatMessage(message.content.msg)}</p>
                                                    ) : null}
                                                    {message.content?.attachments && message.content.attachments.length > 0 && (
                                                        <StaffMessageAttachments
                                                            items={message.content.attachments}
                                                        />
                                                    )}
                                                </>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {new Date(message.createdAt).toLocaleString('pt-BR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false
                                                }).replace(',', ' ')}
                                                {message.editedAt && !message.deletedAt && <span className="italic">(editada)</span>}
                                            </p>
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>
                            )}
                        </div>

                        {hasNewMessages && (
                            <div className="flex justify-center mb-4">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg"
                                    onClick={scrollToBottom}
                                >
                                    <MessageSquare className="h-4 w-4 mr-2" />
                                    Novas mensagens
                                </Button>
                            </div>
                        )}

                        <div className="px-6 pb-6">
                            <div className={`space-y-2 p-3 rounded-lg transition-colors ${messageType === "private"
                                ? "bg-amber-50/95 border border-amber-200 dark:bg-amber-950/50 dark:border-amber-800"
                                : "bg-muted/50"
                                }`}>
                                <RadioGroup
                                    value={messageType}
                                    onValueChange={(value) => setMessageType(value as "public" | "private")}
                                    className="flex gap-4"
                                >
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="public" id="public-desktop" />
                                        <Label
                                            htmlFor="public-desktop"
                                            className={`transition-colors text-sm ${messageType === "private" ? "text-muted-foreground" : "text-foreground"
                                                }`}
                                        >
                                            Mensagem Pública
                                        </Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="private" id="private-desktop" />
                                        <Label
                                            htmlFor="private-desktop"
                                            className={`transition-colors text-sm ${messageType === "private"
                                                ? "text-yellow-600 font-medium"
                                                : "text-muted-foreground"
                                                }`}
                                        >
                                            Nota Privada
                                        </Label>
                                    </div>
                                </RadioGroup>

                                {pendingFiles.length > 0 && (
                                    <ul className="text-sm border rounded-md p-2 max-h-32 overflow-y-auto">
                                        {pendingFiles.map((f, i) => (
                                            <PendingFileRow
                                                key={`${f.name}-${i}`}
                                                file={f}
                                                onRemove={() =>
                                                    setPendingFiles((prev) => prev.filter((_, j) => j !== i))
                                                }
                                            />
                                        ))}
                                    </ul>
                                )}

                                <div className="flex flex-wrap gap-2 items-center">
                                    <label
                                        className={`cursor-pointer inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground ${isRecording ? "pointer-events-none opacity-50" : ""
                                            }`}
                                    >
                                        <Paperclip className="h-4 w-4" />
                                        Anexar
                                        <input
                                            type="file"
                                            className="hidden"
                                            multiple
                                            disabled={isRecording}
                                            onChange={(e) => {
                                                const files = Array.from(e.target.files || []);
                                                setPendingFiles((prev) => [...prev, ...files]);
                                                e.target.value = "";
                                            }}
                                        />
                                    </label>
                                    {!isRecording ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-8 gap-1"
                                            disabled={isSendingMessage}
                                            onClick={startAgentRecording}
                                        >
                                            <Mic className="h-3 w-3" />
                                            Gravar
                                        </Button>
                                    ) : (
                                        <Button type="button" variant="destructive" size="sm" className="h-8 gap-1" onClick={stopAgentRecording}>
                                            <Square className="h-3 w-3" />
                                            Parar
                                        </Button>
                                    )}
                                </div>

                                <div className="flex gap-2 items-end">
                                    <MentionTextarea
                                        ref={composerTextareaDesktopRef}
                                        agents={agents}
                                        rows={1}
                                        placeholder={messageType === "public" ? "Digite sua mensagem..." : "Digite sua nota privada..."}
                                        value={newMessage}
                                        onValueChange={setNewMessage}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                        onPaste={handlePasteImages}
                                        disabled={isSendingMessage || isRecording}
                                        style={{ maxHeight: MESSAGE_TEXTAREA_MAX_HEIGHT }}
                                        className={`min-h-[40px] resize-none overflow-y-auto py-2 transition-colors text-sm ${messageType === "private"
                                            ? "bg-amber-50 text-zinc-900 border-amber-300 placeholder:text-zinc-500 focus-visible:ring-amber-400/50 dark:bg-amber-950 dark:text-amber-50 dark:border-amber-600 dark:placeholder:text-amber-200/70"
                                            : ""
                                            } ${isSendingMessage || isRecording ? "opacity-50 cursor-not-allowed" : ""}`}
                                    />
                                    <Button
                                        onClick={handleSendMessage}
                                        disabled={
                                            (!newMessage.trim() && pendingFiles.length === 0) ||
                                            isSendingMessage ||
                                            isRecording
                                        }
                                        size="icon"
                                        className={`transition-colors ${messageType === "private"
                                            ? "bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-600 dark:hover:bg-amber-500"
                                            : ""
                                            } ${isSendingMessage ? "opacity-50" : ""}`}
                                    >
                                        {isSendingMessage ? (
                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Send className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Informações do Ticket */}
                <div className="col-span-1 h-full overflow-auto px-6 py-4">
                    <div className="space-y-4">
                        <div className="space-y-4">
                            <SlaResponseBadge ticket={ticket} />

                            <div className="space-y-2">
                                <Label className="text-sm">Status</Label>
                                <Select value={selectedStatus} onValueChange={(value) => {
                                    setSelectedStatus(value);
                                    if (value === "resolved") {
                                        setIsResolutionDialogOpen(true);
                                    }
                                }}>
                                    <SelectTrigger className="text-sm">
                                        <SelectValue>
                                            {selectedStatus === "open" && "Aberto"}
                                            {selectedStatus === "in_progress" && "Em Atendimento"}
                                            {selectedStatus === "resolved" && "Resolvido"}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="open">Aberto</SelectItem>
                                        <SelectItem value="in_progress">Em Atendimento</SelectItem>
                                        <SelectItem value="resolved">Resolvido</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm">Empresa</Label>
                                <Select
                                    value={selectedClient || undefined}
                                    onValueChange={(value) => {
                                        setSelectedClient(value);
                                        setSelectedProject("");
                                        setSelectedContact("");
                                    }}
                                >
                                    <SelectTrigger className="text-sm">
                                        <SelectValue placeholder="Selecione uma empresa">
                                            {companies.find((c) => String(c._id) === String(selectedClient))?.name ||
                                                ticket?.clientName ||
                                                "Selecione uma empresa"}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {companies.map((company) => (
                                            <SelectItem key={company._id} value={company._id}>
                                                {company.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm">Projeto</Label>
                                <Select
                                    value={selectedProject || undefined}
                                    onValueChange={setSelectedProject}
                                    disabled={!selectedClient}
                                >
                                    <SelectTrigger className="text-sm">
                                        <SelectValue placeholder={selectedClient ? "Selecione um projeto" : "Selecione uma empresa primeiro"}>
                                            {projects.find((p) => String(p._id) === String(selectedProject))?.name ||
                                                ticket?.projectName}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {projects.map((project) => (
                                            <SelectItem key={project._id} value={project._id}>
                                                {project.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm">Pessoa</Label>
                                <Select
                                    value={selectedContact || undefined}
                                    onValueChange={setSelectedContact}
                                    disabled={!selectedClient}
                                >
                                    <SelectTrigger className="text-sm">
                                        <SelectValue placeholder={selectedClient ? "Selecione uma pessoa" : "Selecione uma empresa primeiro"}>
                                            {contacts.find((c) => String(c._id) === String(selectedContact))?.name ||
                                                ticket?.contactName}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {contacts.map((contact) => (
                                            <SelectItem key={contact._id} value={contact._id}>
                                                {contact.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {(ticket?.contactPhone || ticket?.contactEmail || ticket?.contactTags?.length) && (
                                <div className="space-y-2 rounded-lg border p-3 text-sm">
                                    <Label className="text-sm">Contato</Label>
                                    {ticket.contactPhone && (
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Phone className="h-3.5 w-3.5 shrink-0" />
                                            <span className="truncate">{ticket.contactPhone}</span>
                                        </div>
                                    )}
                                    {ticket.contactEmail && (
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Mail className="h-3.5 w-3.5 shrink-0" />
                                            <span className="truncate">{ticket.contactEmail}</span>
                                        </div>
                                    )}
                                    {!!ticket.contactTags?.length && (
                                        <div className="flex flex-wrap gap-1 pt-1">
                                            {ticket.contactTags.map((tag) => (
                                                <Badge key={tag} variant="outline" className="text-xs">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label className="text-sm">Equipe</Label>
                                <Select value={selectedTeam || undefined} onValueChange={setSelectedTeam}>
                                    <SelectTrigger className="text-sm">
                                        <SelectValue placeholder="Selecione uma equipe">
                                            {teams.find((t) => String(t._id) === String(selectedTeam))?.name}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {teams.map((team) => (
                                            <SelectItem key={team._id} value={team._id}>
                                                {team.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm">Responsável</Label>
                                <Select
                                    value={selectedAgent ? selectedAgent : "none"}
                                    onValueChange={(value) => {
                                        setSelectedAgent(value === "none" ? "" : value);
                                    }}
                                >
                                    <SelectTrigger className="text-sm">
                                        <SelectValue placeholder="Selecione um responsável">
                                            {selectedAgent
                                                ? agents.find((a) => String(a._id) === String(selectedAgent))?.name
                                                : "Nenhum responsável específico"}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Nenhum responsável específico</SelectItem>
                                        {agents.map((agent) => (
                                            <SelectItem key={agent._id} value={agent._id}>
                                                {agent.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <TagPoolField
                                id="ticket-tags"
                                label="Tags do ticket"
                                value={selectedTags}
                                onChange={setSelectedTags}
                            />
                        </div>

                        <div className="pt-4 border-t">
                            <Button className="w-full text-sm" keyLoader="updateTicket" onClick={() => handleUpdateTicket()}>
                                Atualizar Ticket
                            </Button>
                        </div>

                        {/* Pessoas no ticket */}
                        <div className="pt-4 border-t space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm flex items-center gap-1.5">
                                    <UserPlus className="h-3.5 w-3.5" />
                                    Pessoas no ticket
                                </Label>
                                {selectedClient && (
                                    <Dialog
                                        open={addMembersOpen}
                                        onOpenChange={(open) => {
                                            setAddMembersOpen(open);
                                            if (!open) { setSelectedMemberIds([]); setMemberSearch(""); }
                                        }}
                                    >
                                        <DialogTrigger asChild>
                                            <Button variant="outline" size="sm" className="h-7 text-xs px-2">
                                                Adicionar
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Adicionar pessoas ao ticket</DialogTitle>
                                            </DialogHeader>
                                            <div className="space-y-3">
                                                <Input
                                                    placeholder="Buscar por nome..."
                                                    value={memberSearch}
                                                    onChange={(e) => setMemberSearch(e.target.value)}
                                                />
                                                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                                                    {availableMembersToAdd.map((c) => (
                                                        <label
                                                            key={c._id}
                                                            className="flex items-center gap-2 cursor-pointer rounded-md px-2 py-1.5 hover:bg-muted/40"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedMemberIds.includes(c._id)}
                                                                onChange={(e) =>
                                                                    setSelectedMemberIds((prev) =>
                                                                        e.target.checked
                                                                            ? [...prev, c._id]
                                                                            : prev.filter((x) => x !== c._id)
                                                                    )
                                                                }
                                                            />
                                                            <span className="text-sm">{c.name}</span>
                                                        </label>
                                                    ))}
                                                    {availableMembersToAdd.length === 0 && (
                                                        <p className="text-sm text-muted-foreground px-2">
                                                            {contacts.length === 0
                                                                ? "Nenhuma pessoa cadastrada nesta empresa."
                                                                : "Nenhuma pessoa disponível para adicionar."}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <DialogFooter>
                                                <Button variant="outline" onClick={() => setAddMembersOpen(false)}>
                                                    Cancelar
                                                </Button>
                                                <Button
                                                    onClick={handleAddMembers}
                                                    disabled={isAddingMembers || selectedMemberIds.length === 0}
                                                >
                                                    {isAddingMembers ? "Adicionando..." : "Adicionar"}
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {(ticket.memberIds || []).length === 0 && (
                                    <p className="text-xs text-muted-foreground">Nenhuma pessoa adicionada.</p>
                                )}
                                {(ticket.memberIds || []).map((mid) => {
                                    const midStr = String(mid);
                                    const memberContact = contacts.find((c) => String(c._id) === midStr);
                                    return (
                                        <Badge
                                            key={midStr}
                                            variant="secondary"
                                            className="gap-1 items-center py-1 px-2 h-auto"
                                        >
                                            <span className="text-xs font-medium">
                                                {memberContact?.name || "Pessoa"}
                                            </span>
                                            <button
                                                type="button"
                                                className="ml-0.5 text-muted-foreground hover:text-destructive"
                                                onClick={() => handleRemoveMember(midStr)}
                                                aria-label="Remover pessoa"
                                            >
                                                ×
                                            </button>
                                        </Badge>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Layout Mobile/Tablet */}
            <div className="lg:hidden flex-1 h-[calc(100vh-8rem)] overflow-hidden">
                <Tabs defaultValue="chat" className="h-full flex flex-col">
                    <TabsList className="w-full justify-start px-0 border-b rounded-none h-12 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shrink-0">
                        <TabsTrigger value="chat" className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none h-12">
                            <div className="flex flex-col items-center gap-1">
                                <MessageSquare className="h-4 w-4" />
                                <span className="text-xs">Conversa</span>
                            </div>
                        </TabsTrigger>
                        <TabsTrigger value="info" className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none h-12">
                            <div className="flex flex-col items-center gap-1">
                                <Info className="h-4 w-4" />
                                <span className="text-xs">Informações</span>
                            </div>
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent
                        value="chat"
                        className="flex-1 mt-0 p-0 flex flex-col data-[state=inactive]:hidden overflow-y-scroll"
                    >
                        <div className="flex-1 overflow-y-scroll mb-4 px-4" ref={messagesContainerRef} onScroll={handleScroll}>
                            {isLoadingMore && (
                                <div className="flex justify-center py-4">
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                </div>
                            )}
                            {messages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                                    <MessageSquare className="h-8 w-8 mb-2" />
                                    <p>Nenhuma mensagem ainda</p>
                                </div>
                            ) : (
                                <div className="space-y-3 py-4">
                                    {messages.map((message) => (
                                        <div
                                            key={message._id}
                                            className={`p-3 rounded-lg ${message.type === MessageType.PRIVATE_NOTE
                                                ? "w-full bg-yellow-50 border border-yellow-200"
                                                : message.type === MessageType.SYSTEM
                                                    ? "bg-purple-100 border border-purple-200 mr-auto max-w-[85%]"
                                                    : isOwnMessage(message)
                                                        ? "bg-primary/5 dark:bg-primary/10 border border-primary/15 dark:border-primary/20 ml-auto max-w-[85%]"
                                                        : "bg-muted/50 mr-auto max-w-[85%]"
                                                }`}
                                        >
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                {message.type === MessageType.SYSTEM ? (
                                                    <div className="flex items-center gap-2">
                                                        <Bot className="h-4 w-4 text-purple-600" />
                                                        <span className="text-sm font-medium text-purple-600">IA</span>
                                                    </div>
                                                ) : message.type === MessageType.AGENT ? (
                                                    <div className="flex items-center gap-2">
                                                        <User className="h-4 w-4 text-primary" />
                                                        <span className="text-sm font-medium text-primary">
                                                            {message.agent?.name || "Agente"}
                                                        </span>
                                                    </div>
                                                ) : message.type === MessageType.PRIVATE_NOTE ? (
                                                    <div className="flex items-center gap-2">
                                                        <MessageSquare className="h-4 w-4 text-yellow-600" />
                                                        <span className="text-sm font-medium text-yellow-600">
                                                            Nota Privada
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-sm font-medium text-muted-foreground">
                                                            {message.contact?.name || "Cliente"}
                                                        </span>
                                                    </div>
                                                )}
                                                {canEditMessage(message) && editingMessageId !== message._id && (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => startEditMessage(message)}
                                                            className="text-muted-foreground hover:text-foreground"
                                                            aria-label="Editar mensagem"
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteMessage(message)}
                                                            disabled={deletingMessageId === message._id}
                                                            className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                                                            aria-label="Excluir mensagem"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {message.deletedAt ? (
                                                <p className="text-sm italic text-muted-foreground">Mensagem excluída</p>
                                            ) : editingMessageId === message._id ? (
                                                <div className="space-y-2">
                                                    <textarea
                                                        className="w-full text-sm rounded-md border border-input bg-background p-2 resize-none"
                                                        rows={3}
                                                        value={editingText}
                                                        onChange={(e) => setEditingText(e.target.value)}
                                                        autoFocus
                                                    />
                                                    <div className="flex gap-2">
                                                        <Button size="sm" className="h-7 gap-1" disabled={savingEdit} onClick={saveEditMessage}>
                                                            <Check className="h-3 w-3" />
                                                            Salvar
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="h-7 gap-1" disabled={savingEdit} onClick={cancelEditMessage}>
                                                            <X className="h-3 w-3" />
                                                            Cancelar
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {message.content?.msg ? (
                                                        <p className={`text-sm whitespace-pre-wrap ${message.type === MessageType.PRIVATE_NOTE
                                                            ? "text-yellow-800"
                                                            : message.type === MessageType.SYSTEM
                                                                ? "text-purple-800"
                                                                : ""
                                                            }`}>{formatMessage(message.content.msg)}</p>
                                                    ) : null}
                                                    {message.content?.attachments && message.content.attachments.length > 0 && (
                                                        <StaffMessageAttachments
                                                            items={message.content.attachments}
                                                        />
                                                    )}
                                                </>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {new Date(message.createdAt).toLocaleString('pt-BR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false
                                                }).replace(',', ' ')}
                                                {message.editedAt && !message.deletedAt && <span className="italic">(editada)</span>}
                                            </p>
                                        </div>
                                    ))}
                                    <div ref={messagesEndMobileRef} />
                                </div>
                            )}
                        </div>

                        {hasNewMessages && (
                            <div className="flex justify-center mb-4">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg"
                                    onClick={scrollToBottom}
                                >
                                    <MessageSquare className="h-4 w-4 mr-2" />
                                    Novas mensagens
                                </Button>
                            </div>
                        )}

                        <div className="px-4 pb-4 sticky bottom-0 bg-background">
                            <div className={`space-y-2 p-3 rounded-lg transition-colors ${messageType === "private"
                                ? "bg-amber-50/95 border border-amber-200 dark:bg-amber-950/50 dark:border-amber-800"
                                : "bg-muted/50"
                                }`}>
                                <RadioGroup
                                    value={messageType}
                                    onValueChange={(value) => setMessageType(value as "public" | "private")}
                                    className="flex gap-4"
                                >
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="public" id="public-mobile" />
                                        <Label
                                            htmlFor="public-mobile"
                                            className={`transition-colors text-sm ${messageType === "private" ? "text-muted-foreground" : "text-foreground"
                                                }`}
                                        >
                                            Mensagem Pública
                                        </Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="private" id="private-mobile" />
                                        <Label
                                            htmlFor="private-mobile"
                                            className={`transition-colors text-sm ${messageType === "private"
                                                ? "text-yellow-600 font-medium"
                                                : "text-muted-foreground"
                                                }`}
                                        >
                                            Nota Privada
                                        </Label>
                                    </div>
                                </RadioGroup>

                                {pendingFiles.length > 0 && (
                                    <ul className="text-sm border rounded-md p-2 max-h-32 overflow-y-auto">
                                        {pendingFiles.map((f, i) => (
                                            <PendingFileRow
                                                key={`m-${f.name}-${i}`}
                                                file={f}
                                                onRemove={() =>
                                                    setPendingFiles((prev) => prev.filter((_, j) => j !== i))
                                                }
                                            />
                                        ))}
                                    </ul>
                                )}

                                <div className="flex flex-wrap gap-2 items-center">
                                    <label
                                        className={`cursor-pointer inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground ${isRecording ? "pointer-events-none opacity-50" : ""
                                            }`}
                                    >
                                        <Paperclip className="h-4 w-4" />
                                        Anexar
                                        <input
                                            type="file"
                                            className="hidden"
                                            multiple
                                            disabled={isRecording}
                                            onChange={(e) => {
                                                const files = Array.from(e.target.files || []);
                                                setPendingFiles((prev) => [...prev, ...files]);
                                                e.target.value = "";
                                            }}
                                        />
                                    </label>
                                    {!isRecording ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-8 gap-1"
                                            disabled={isSendingMessage}
                                            onClick={startAgentRecording}
                                        >
                                            <Mic className="h-3 w-3" />
                                            Gravar
                                        </Button>
                                    ) : (
                                        <Button type="button" variant="destructive" size="sm" className="h-8 gap-1" onClick={stopAgentRecording}>
                                            <Square className="h-3 w-3" />
                                            Parar
                                        </Button>
                                    )}
                                </div>

                                <div className="flex gap-2 items-end">
                                    <MentionTextarea
                                        ref={composerTextareaMobileRef}
                                        agents={agents}
                                        rows={1}
                                        placeholder={messageType === "public" ? "Digite sua mensagem..." : "Digite sua nota privada..."}
                                        value={newMessage}
                                        onValueChange={setNewMessage}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                        onPaste={handlePasteImages}
                                        disabled={isSendingMessage || isRecording}
                                        style={{ maxHeight: MESSAGE_TEXTAREA_MAX_HEIGHT }}
                                        className={`min-h-[40px] resize-none overflow-y-auto py-2 transition-colors text-sm ${messageType === "private"
                                            ? "bg-amber-50 text-zinc-900 border-amber-300 placeholder:text-zinc-500 focus-visible:ring-amber-400/50 dark:bg-amber-950 dark:text-amber-50 dark:border-amber-600 dark:placeholder:text-amber-200/70"
                                            : ""
                                            } ${isSendingMessage || isRecording ? "opacity-50 cursor-not-allowed" : ""}`}
                                    />
                                    <Button
                                        onClick={handleSendMessage}
                                        disabled={
                                            (!newMessage.trim() && pendingFiles.length === 0) ||
                                            isSendingMessage ||
                                            isRecording
                                        }
                                        size="icon"
                                        className={`transition-colors ${messageType === "private"
                                            ? "bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-600 dark:hover:bg-amber-500"
                                            : ""
                                            } ${isSendingMessage ? "opacity-50" : ""}`}
                                    >
                                        {isSendingMessage ? (
                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Send className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent
                        value="info"
                        className="flex-1 mt-0 overflow-y-auto data-[state=inactive]:hidden"
                    >
                        <div className="space-y-4 p-4 pt-0">
                            <div className="space-y-4">
                                <SlaResponseBadge ticket={ticket} />

                                <div className="space-y-2">
                                    <Label className="text-sm">Status</Label>
                                    <Select value={selectedStatus} onValueChange={(value) => {
                                        setSelectedStatus(value);
                                        if (value === "resolved") {
                                            setIsResolutionDialogOpen(true);
                                        }
                                    }}>
                                        <SelectTrigger className="text-sm">
                                            <SelectValue>
                                                {selectedStatus === "open" && "Aberto"}
                                                {selectedStatus === "in_progress" && "Em Atendimento"}
                                                {selectedStatus === "resolved" && "Resolvido"}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="open">Aberto</SelectItem>
                                            <SelectItem value="in_progress">Em Atendimento</SelectItem>
                                            <SelectItem value="resolved">Resolvido</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm">Empresa</Label>
                                    <Select
                                        value={selectedClient || undefined}
                                        onValueChange={(value) => {
                                            setSelectedClient(value);
                                            setSelectedProject("");
                                            setSelectedContact("");
                                        }}
                                    >
                                        <SelectTrigger className="text-sm">
                                            <SelectValue placeholder="Selecione uma empresa">
                                                {companies.find((c) => String(c._id) === String(selectedClient))?.name ||
                                                    ticket?.clientName ||
                                                    "Selecione uma empresa"}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {companies.map((company) => (
                                                <SelectItem key={company._id} value={company._id}>
                                                    {company.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm">Projeto</Label>
                                    <Select
                                        value={selectedProject || undefined}
                                        onValueChange={setSelectedProject}
                                        disabled={!selectedClient}
                                    >
                                        <SelectTrigger className="text-sm">
                                            <SelectValue placeholder={selectedClient ? "Selecione um projeto" : "Selecione uma empresa primeiro"}>
                                                {projects.find((p) => String(p._id) === String(selectedProject))?.name ||
                                                    ticket?.projectName}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {projects.map((project) => (
                                                <SelectItem key={project._id} value={project._id}>
                                                    {project.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm">Pessoa</Label>
                                    <Select
                                        value={selectedContact || undefined}
                                        onValueChange={setSelectedContact}
                                        disabled={!selectedClient}
                                    >
                                        <SelectTrigger className="text-sm">
                                            <SelectValue placeholder={selectedClient ? "Selecione uma pessoa" : "Selecione uma empresa primeiro"}>
                                                {contacts.find((c) => String(c._id) === String(selectedContact))?.name ||
                                                    ticket?.contactName}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {contacts.map((contact) => (
                                                <SelectItem key={contact._id} value={contact._id}>
                                                    {contact.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {(ticket?.contactPhone || ticket?.contactEmail || ticket?.contactTags?.length) && (
                                    <div className="space-y-2 rounded-lg border p-3 text-sm">
                                        <Label className="text-sm">Contato</Label>
                                        {ticket.contactPhone && (
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                                <Phone className="h-3.5 w-3.5 shrink-0" />
                                                <span className="truncate">{ticket.contactPhone}</span>
                                            </div>
                                        )}
                                        {ticket.contactEmail && (
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                                <Mail className="h-3.5 w-3.5 shrink-0" />
                                                <span className="truncate">{ticket.contactEmail}</span>
                                            </div>
                                        )}
                                        {!!ticket.contactTags?.length && (
                                            <div className="flex flex-wrap gap-1 pt-1">
                                                {ticket.contactTags.map((tag) => (
                                                    <Badge key={tag} variant="outline" className="text-xs">
                                                        {tag}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label className="text-sm">Equipe</Label>
                                    <Select value={selectedTeam || undefined} onValueChange={setSelectedTeam}>
                                        <SelectTrigger className="text-sm">
                                            <SelectValue placeholder="Selecione uma equipe">
                                                {teams.find((t) => String(t._id) === String(selectedTeam))?.name}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {teams.map((team) => (
                                                <SelectItem key={team._id} value={team._id}>
                                                    {team.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm">Responsável</Label>
                                    <Select
                                        value={selectedAgent ? selectedAgent : "none"}
                                        onValueChange={(value) => {
                                            setSelectedAgent(value === "none" ? "" : value);
                                        }}
                                    >
                                        <SelectTrigger className="text-sm">
                                            <SelectValue placeholder="Selecione um responsável">
                                                {selectedAgent
                                                    ? agents.find((a) => String(a._id) === String(selectedAgent))?.name
                                                    : "Nenhum responsável específico"}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Nenhum responsável específico</SelectItem>
                                            {agents.map((agent) => (
                                                <SelectItem key={agent._id} value={agent._id}>
                                                    {agent.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <TagPoolField
                                    id="ticket-tags-mobile"
                                    label="Tags do ticket"
                                    value={selectedTags}
                                    onChange={setSelectedTags}
                                />
                            </div>

                            <div className="pt-4 border-t">
                                <Button className="w-full text-sm" keyLoader="updateTicket" onClick={() => handleUpdateTicket()}>
                                    Atualizar Ticket
                                </Button>
                            </div>

                            {/* Pessoas no ticket — mobile */}
                            <div className="pt-4 border-t space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm flex items-center gap-1.5">
                                        <UserPlus className="h-3.5 w-3.5" />
                                        Pessoas no ticket
                                    </Label>
                                    {selectedClient && (
                                        <Dialog
                                            open={addMembersOpen}
                                            onOpenChange={(open) => {
                                                setAddMembersOpen(open);
                                                if (!open) { setSelectedMemberIds([]); setMemberSearch(""); }
                                            }}
                                        >
                                            <DialogTrigger asChild>
                                                <Button variant="outline" size="sm" className="h-7 text-xs px-2">
                                                    Adicionar
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent>
                                                <DialogHeader>
                                                    <DialogTitle>Adicionar pessoas ao ticket</DialogTitle>
                                                </DialogHeader>
                                                <div className="space-y-3">
                                                    <Input
                                                        placeholder="Buscar por nome..."
                                                        value={memberSearch}
                                                        onChange={(e) => setMemberSearch(e.target.value)}
                                                    />
                                                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                                                        {availableMembersToAdd.map((c) => (
                                                            <label
                                                                key={c._id}
                                                                className="flex items-center gap-2 cursor-pointer rounded-md px-2 py-1.5 hover:bg-muted/40"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedMemberIds.includes(c._id)}
                                                                    onChange={(e) =>
                                                                        setSelectedMemberIds((prev) =>
                                                                            e.target.checked
                                                                                ? [...prev, c._id]
                                                                                : prev.filter((x) => x !== c._id)
                                                                        )
                                                                    }
                                                                />
                                                                <span className="text-sm">{c.name}</span>
                                                            </label>
                                                        ))}
                                                        {availableMembersToAdd.length === 0 && (
                                                            <p className="text-sm text-muted-foreground px-2">
                                                                {contacts.length === 0
                                                                    ? "Nenhuma pessoa cadastrada nesta empresa."
                                                                    : "Nenhuma pessoa disponível para adicionar."}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <DialogFooter>
                                                    <Button variant="outline" onClick={() => setAddMembersOpen(false)}>
                                                        Cancelar
                                                    </Button>
                                                    <Button
                                                        onClick={handleAddMembers}
                                                        disabled={isAddingMembers || selectedMemberIds.length === 0}
                                                    >
                                                        {isAddingMembers ? "Adicionando..." : "Adicionar"}
                                                    </Button>
                                                </DialogFooter>
                                            </DialogContent>
                                        </Dialog>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(ticket.memberIds || []).length === 0 && (
                                        <p className="text-xs text-muted-foreground">Nenhuma pessoa adicionada.</p>
                                    )}
                                    {(ticket.memberIds || []).map((mid) => {
                                        const midStr = String(mid);
                                        const memberContact = contacts.find((c) => String(c._id) === midStr);
                                        return (
                                            <Badge
                                                key={midStr}
                                                variant="secondary"
                                                className="gap-1 items-center py-1 px-2 h-auto"
                                            >
                                                <span className="text-xs font-medium">
                                                    {memberContact?.name || "Pessoa"}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="ml-0.5 text-muted-foreground hover:text-destructive"
                                                    onClick={() => handleRemoveMember(midStr)}
                                                    aria-label="Remover pessoa"
                                                >
                                                    ×
                                                </button>
                                            </Badge>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            <Dialog open={isResolutionDialogOpen} onOpenChange={setIsResolutionDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Tempo Real de Resolução</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            Opcional: informe o tempo efetivo de atendimento (horas e minutos). Deixe em branco ou zero se não quiser registrar. Confirme para gravar o status como resolvido.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="hours">Horas</Label>
                                <Input
                                    id="hours"
                                    type="number"
                                    min="0"
                                    value={resolutionTime.hours}
                                    onChange={(e) => setResolutionTime({ ...resolutionTime, hours: e.target.value })}
                                    placeholder="0"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="minutes">Minutos</Label>
                                <Input
                                    id="minutes"
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={resolutionTime.minutes}
                                    onChange={(e) => setResolutionTime({ ...resolutionTime, minutes: e.target.value })}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setIsResolutionDialogOpen(false);
                            setSelectedStatus(ticket?.status || "open");
                        }}>
                            Cancelar
                        </Button>
                        <Button onClick={handleResolution}>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Confirmar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
} 